import type { SyncMeta } from '../store/types'

/**
 * Last-write-wins merge of one collection.
 *
 * Taken from `../semainier/src/sync/merge.ts`. Conflicts are rarer there — one
 * teacher with a tablet and a PC — and genuinely common here, where two people
 * tick boxes in the same house. Last-write-wins is still the right answer for a
 * checkbox: it is idempotent enough that the winner and the loser usually
 * agree. What matters is that the rule is predictable and never loses a row
 * silently:
 *
 *   - a row present on one side only is kept;
 *   - when both sides have it, the newer `updatedAt` wins outright;
 *   - a local row that wins stays `dirty`, so the next push carries it up;
 *   - a remote row that wins lands `dirty: false`, since it came from the
 *     server and is already stored there.
 *
 * `updatedAt` is assigned by Postgres, so after any successful sync both
 * devices are comparing timestamps from the same clock.
 *
 * This replaces the draft's `mergeRemote`, which was `{ ...local, ...remote }`
 * — the remote copy winning unconditionally, so a box ticked offline was
 * un-ticked by the next pull.
 */

export type Row = SyncMeta & { id: string }

export interface MergeResult<T extends Row> {
  rows: T[]
  /** Rows whose remote copy replaced the local one. */
  applied: number
  /** Rows kept because the local edit was newer. */
  kept: number
}

export function mergeCollection<T extends Row>(
  local: readonly T[],
  remote: readonly T[],
): MergeResult<T> {
  const byId = new Map<string, T>()
  for (const row of local) byId.set(row.id, row)

  let applied = 0
  let kept = 0

  for (const incoming of remote) {
    const current = byId.get(incoming.id)

    if (!current) {
      // Unknown here: created on the other device, or created here and since
      // wiped locally. Either way the server copy is the truth.
      byId.set(incoming.id, incoming)
      applied++
      continue
    }

    if (incoming.updatedAt > current.updatedAt) {
      byId.set(incoming.id, incoming)
      applied++
    } else {
      // Local is newer or identical. Identical timestamps keep the local copy,
      // which is harmless: the two rows carry the same content.
      kept++
    }
  }

  return { rows: [...byId.values()], applied, kept }
}

/** Rows with local changes the server has not acknowledged yet. */
export function pendingRows<T extends Row>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.dirty)
}

/**
 * Drop rows deleted long enough ago that no device can still be unaware.
 *
 * Nothing in this app deletes anything today — unchecking is `done: false`.
 * The function is kept because the moment something does, a tombstone that is
 * never purged grows the local store and every sync payload forever.
 */
export const TOMBSTONE_TTL_DAYS = 90

export function purgeTombstones<T extends Row>(rows: readonly T[], now: Date = new Date()): T[] {
  const cutoff = new Date(now.getTime() - TOMBSTONE_TTL_DAYS * 24 * 60 * 60 * 1000).toISOString()

  return rows.filter((r) => {
    if (r.deletedAt === null) return true
    // A deletion not yet pushed must survive regardless of age.
    if (r.dirty) return true
    return r.deletedAt > cutoff
  })
}

/** The newest `updatedAt` seen, used as the next pull cursor. */
export function latestTimestamp(rows: readonly Row[], fallback: string): string {
  let latest = fallback
  for (const row of rows) {
    if (row.updatedAt > latest) latest = row.updatedAt
  }
  return latest
}
